//@flow
import {useState, useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {uploadImageService} from '../../file-storage/promises';

import {
  createBannerGroupService,
  deleteBannerService,
  editBannerGroupService,
} from '../promises';

import {actions} from '../duck';
import {useActions} from '../../../utils/globalStore';
import {compressImageIfNecessary} from '../../../utils/image';

import type {
  Banner,
  BannerContentPosition,
  BannerButtonEntityType,
  BannerGroup,
  BannerShapeType,
} from '../../../entities';
import type {CreateBannerGroupRequest, CreateBannerRequest} from '../api';

import type {RootState} from '../../';
import {useSelector} from 'react-redux';
import {BANNER_MEDIA_SIZES} from '../constants';
import {NEW_BANNER_GROUP_ID} from './home-page-sections/constants';

type BannerImages = {
  desktop: Blob,
  mobile: Blob,
};

type ButtonUrl = {
  identifier: string,
  type: BannerButtonEntityType,
};

export type CreateBannerParams = {
  contentPosition: BannerContentPosition,
  images?: BannerImages,
  buttonUrl: ButtonUrl,
  title: string,
  subtitle: string,
  backgroundColor: string,
  buttonBackgroundColor: string,
  buttonTextColor: string,
  textColor: string,
  buttonText: string,
  videoUrl?: string,
  bannerShapeType: BannerShapeType,
  padded: boolean,
};

export function bannerToBannerRequest(banner: Banner): CreateBannerRequest {
  return {
    bannerMediaType: banner.bannerMediaType || undefined,
    imageId: banner.imageId || undefined,
    imageResponsiveId: banner.imageResponsiveId || undefined,
    videoUrl: banner.videoUrl || undefined,
    title: banner.title || undefined,
    subtitle: banner.subtitle || undefined,
    backgroundColor: banner.backgroundColor || undefined,
    textColor: banner.textColor || undefined,
    bannerTextPosition: banner.bannerTextPosition || undefined,
    buttonText: banner.buttonText || undefined,
    bannerButtonEntityType: banner.bannerButtonEntityType || undefined,
    buttonEntityIdentifier: banner.buttonEntityIdentifier || undefined,
    buttonBackgroundColor: banner.buttonBackgroundColor || undefined,
    buttonTextColor: banner.buttonTextColor || undefined,
    enabled: !!banner.enabled,
    buttonReferenceEntityEnabled: !!banner.buttonReferenceEntityEnabled,
    bannerShapeType: banner.bannerShapeType || 'FLAT',
    padded: !!banner.padded,
  };
}

const bannerGroupActions = {
  addBannerGroup: actions.addBannerGroup,
  updateBannerGroup: actions.updateBannerGroup,
};

export function useBannerGroupActions(): {
  createBanner: (params: CreateBannerParams) => Promise<BannerGroup>,
  toggleEnabledBanner: (
    bannerGroupId: string,
    bannerId: string
  ) => Promise<void>,
  toggleEnabledBannerGroup: (bannerGroupId: string) => Promise<void>,
  reorderBanners: (
    bannerGroupId: string,
    reorderedBanners: Banner[]
  ) => Promise<BannerGroup>,
  loading: boolean,
  banner: Banner | null,
} {
  const {bannerGroupId} = useParams();
  const {addBannerGroup, updateBannerGroup} = useActions(bannerGroupActions);

  const {currentBannerGroup} = useCurrentBannerGroup();
  const {bannerGroups} = useBanners();

  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);

  async function getBannerRequest(
    params: CreateBannerParams
  ): Promise<CreateBannerRequest> {
    const {
      images,
      title,
      subtitle,
      backgroundColor,
      buttonBackgroundColor,
      buttonTextColor,
      textColor,
      buttonText,
      contentPosition,
      bannerShapeType,
      padded,
      buttonUrl: {identifier, type},
      videoUrl,
    } = params;

    const bannerMediaType = videoUrl ? 'VIDEO' : 'IMAGE';

    const bannerRequest: CreateBannerRequest = {
      bannerMediaType,
      videoUrl,
      title,
      subtitle,
      backgroundColor,
      buttonBackgroundColor,
      buttonTextColor,
      textColor,
      buttonText,
      bannerTextPosition: contentPosition,
      buttonEntityIdentifier: identifier,
      bannerButtonEntityType: type,
      enabled: true,
      padded,
      buttonReferenceEntityEnabled: !!identifier,
      bannerShapeType,
    };
    if (bannerMediaType === 'IMAGE' && images) {
      const desktopFile = new File([images.desktop], 'desktop.png', {
        type: 'image/png',
      });
      const mobileFile = new File([images.mobile], 'mobile.png', {
        type: 'image/png',
      });

      const sizeThreshold = 1024 * 1024 * 2;

      const desktopFileCompressed = await compressImageIfNecessary(
        desktopFile,
        {
          maxWidth: BANNER_MEDIA_SIZES[bannerShapeType].desktop.width,
          maxHeight: BANNER_MEDIA_SIZES[bannerShapeType].desktop.height,
          threshold: sizeThreshold,
          quality: 1,
          customFormat: 'jpeg',
        }
      );

      const mobileFileCompressed = await compressImageIfNecessary(mobileFile, {
        maxWidth: BANNER_MEDIA_SIZES[bannerShapeType].mobile.width,
        maxHeight: BANNER_MEDIA_SIZES[bannerShapeType].mobile.height,
        threshold: sizeThreshold,
        quality: 1,
        customFormat: 'jpeg',
      });

      const [desktopImage] = await uploadImageService(
        desktopFileCompressed,
        'ui/banner'
      );

      const [mobileImage] = await uploadImageService(
        mobileFileCompressed,
        'ui/banner'
      );

      bannerRequest.imageId = desktopImage.id;
      bannerRequest.imageResponsiveId = mobileImage.id;
    }

    return bannerRequest;
  }

  async function createBannerGroup(params: CreateBannerParams) {
    setLoading(true);

    try {
      const bannerRequest = await getBannerRequest(params);
      const bannerGroupRequest: CreateBannerGroupRequest = {
        enabled: true,
        banners: [bannerRequest],
      };
      const bannerGroup = await createBannerGroupService(bannerGroupRequest);

      setBanner(bannerGroup);
      addBannerGroup(bannerGroup);
      return bannerGroup;
    } catch (error) {
      console.error(error);
      throw new Error('Error creating banner group');
    } finally {
      setLoading(false);
    }
  }

  async function addBannerToGroup(params: CreateBannerParams) {
    setLoading(true);

    try {
      if (!currentBannerGroup) throw new Error('Banner group not found');
      const newBannerRequest = await getBannerRequest(params);
      const prevBanners = currentBannerGroup.banners
        .map(banner => bannerToBannerRequest(banner))
        .map(banner => ({
          ...banner,
          bannerShapeType: newBannerRequest.bannerShapeType,
          padded: newBannerRequest.padded,
        }));

      const bannerGroupRequest: CreateBannerGroupRequest = {
        enabled: true,
        banners: [...prevBanners, newBannerRequest],
      };
      const bannerGroup = await editBannerGroupService(
        currentBannerGroup.id,
        bannerGroupRequest
      );

      setBanner(bannerGroup);
      updateBannerGroup(bannerGroup);

      return bannerGroup;
    } catch (error) {
      console.error(error);
      throw new Error('Error creating banner group');
    } finally {
      setLoading(false);
    }
  }

  async function toggleEnabledBanner(bannerGroupId: string, bannerId: string) {
    setLoading(true);
    try {
      const bannerGroup = bannerGroups.find(
        bannerGroup => bannerGroup.id === bannerGroupId
      );
      if (!bannerGroup) throw new Error('Banner group not found');
      const bannerGroupRequest: CreateBannerGroupRequest = {
        enabled: bannerGroup.enabled,
        banners: bannerGroup.banners

          .map(banner => {
            if (banner.id === bannerId) {
              return {
                ...banner,
                enabled: !banner.enabled,
              };
            }
            return banner;
          })
          .map(banner => bannerToBannerRequest(banner)),
      };

      const bannerGroupUpdated = await editBannerGroupService(
        bannerGroup.id,
        bannerGroupRequest
      );

      setBanner(bannerGroupUpdated);
      updateBannerGroup(bannerGroupUpdated);

      return bannerGroupUpdated;
    } catch (error) {
      console.error(error);
      throw new Error('Error creating banner group');
    } finally {
      setLoading(false);
    }
  }

  async function toggleEnabledBannerGroup(bannerGroupId: string) {
    setLoading(true);
    try {
      const bannerGroup = bannerGroups.find(
        bannerGroup => bannerGroup.id === bannerGroupId
      );
      if (!bannerGroup) throw new Error('Banner group not found');
      const bannerGroupRequest: CreateBannerGroupRequest = {
        enabled: !bannerGroup.enabled,
        banners: bannerGroup.banners.map(banner =>
          bannerToBannerRequest(banner)
        ),
      };

      const bannerGroupUpdated = await editBannerGroupService(
        bannerGroup.id,
        bannerGroupRequest
      );

      setBanner(bannerGroupUpdated);
      updateBannerGroup(bannerGroupUpdated);
    } catch (error) {
      console.error(error);
      throw new Error('Error creating banner group');
    } finally {
      setLoading(false);
    }
  }

  async function createBanner(params: CreateBannerParams) {
    if (bannerGroupId === NEW_BANNER_GROUP_ID) {
      const bannerGroup = await createBannerGroup(params);
      return bannerGroup;
    } else {
      const bannerGroup = await addBannerToGroup(params);
      return bannerGroup;
    }
  }

  async function reorderBanners(
    bannerGroupIdToReorder: string,
    reorderedBanners: Banner[]
  ) {
    setLoading(true);
    try {
      const bannerGroup = bannerGroups.find(
        bg => bg.id === bannerGroupIdToReorder
      );
      if (!bannerGroup) throw new Error('Banner group not found');

      const bannerGroupRequest: CreateBannerGroupRequest = {
        enabled: bannerGroup.enabled,
        banners: reorderedBanners.map(banner => bannerToBannerRequest(banner)),
      };

      const bannerGroupUpdated = await editBannerGroupService(
        bannerGroupIdToReorder,
        bannerGroupRequest
      );

      updateBannerGroup(bannerGroupUpdated);
      return bannerGroupUpdated;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    createBanner,
    loading,
    banner,
    toggleEnabledBanner,
    toggleEnabledBannerGroup,
    reorderBanners,
  };
}

const bannerGroupSelector = (state: RootState) => state.homePage.bannerGroups;

export function useCurrentBannerGroup() {
  const {bannerGroupId} = useParams();
  const bannerGroups = useSelector(bannerGroupSelector);

  const currentBannerGroup = useMemo(() => {
    if (bannerGroupId === NEW_BANNER_GROUP_ID) {
      return null;
    }

    return bannerGroups.find(bannerGroup => bannerGroup.id === bannerGroupId);
  }, [bannerGroupId, bannerGroups]);

  return {
    currentBannerGroup,
  };
}

const bannerSelector = (state: RootState) => ({
  bannerGroups: state.homePage.bannerGroups || [],
  loading: state.homePage.isFetchingBanners,
});

export function useBanners(): {
  bannerGroups: BannerGroup[],
  loading: boolean,
} {
  const {bannerGroups, loading} = useSelector(bannerSelector);

  return {bannerGroups, loading};
}

export function useGetBannerGroups() {
  const getBannerGroups = useActions(actions.getBanners);

  useEffect(() => {
    getBannerGroups();
  }, [getBannerGroups]);
}

const bannerActions = {
  openBanner: actions.openBanner,
  closeBanner: actions.closeBanner,
  deleteBanner: actions.deleteBanner,
};

export function useBannerActions() {
  const [loading, setLoading] = useState(false);
  const {
    openBanner,
    closeBanner,
    deleteBanner: deleteBannerAction,
  } = useActions(bannerActions);

  async function deleteBanner(bannerGroupId: string, id: string) {
    setLoading(true);

    try {
      await deleteBannerService(id);
      deleteBannerAction(bannerGroupId, id);
    } catch (error) {
      console.log(error);
      throw new Error('Error deleting banner');
    } finally {
      setLoading(false);
    }
  }

  return {openBanner, closeBanner, deleteBanner, loading};
}

export function useBannerOpened(): Banner | null {
  const banner = useSelector((state: RootState) => state.homePage.bannerOpened);

  return banner || null;
}
